import React from "react";
import {
  FiPackage,
  FiTruck,
  FiMapPin,
  FiCheckCircle,
  FiAlertTriangle,
  FiClock,
} from "react-icons/fi";

export const OrderHistoryStatusBar = ({
  status,
  toggleRowExpandedProps,
  isLoading,
  isError,
}) => {
  const EXCEPTION_STATUSES = [
    "Delivered with Possible Exception",
    "Delivered**",
    "Possible Exception",
    "Exception",
  ];
  const WARNING_STATUSES = ["-Delivered-"];
  const PARTIAL_STATUSES = ["Partial Delivery"];
  const isException = EXCEPTION_STATUSES.includes(status);
  const isWarning = WARNING_STATUSES.includes(status);
  const isPartial = PARTIAL_STATUSES.includes(status);

  const statusLevel = {
    Open: 0,
    "In Transit": 1,
    "Picked Up": 1,
    "Out for Delivery": 2,
    Delivered: 3,
    "Delivered with Possible Exception": 3,
    "Delivered**": 3,
    Exception: 3,
    "Possible Exception": 3,
    "-Delivered-": 3,
    "Partial Delivery": 3,
  };

  const inTransitStepLabel =
    status === "Picked Up" ? "Picked Up" : "In Transit"; //will say picked up if that's the latest status but will stay as in transit after that.
  const lastStepLabel = isException
    ? "Possible Exception"
    : isPartial
      ? "Partial Delivery"
      : "Delivered";
  const lastStepIcon = isException
    ? FiAlertTriangle
    : isWarning
      ? FiClock
      : FiCheckCircle;

  const steps = [
    { label: "Open", icon: FiPackage },
    { label: inTransitStepLabel, icon: FiTruck },
    { label: "Out for Delivery", icon: FiMapPin },
    { label: lastStepLabel, icon: lastStepIcon },
  ];

  const currentLevel = statusLevel[status] ?? 0;

  const KNOWN_STATUSES = Object.keys(statusLevel);
  if (status && !KNOWN_STATUSES.includes(status)) {
    return (
      <span style={{ fontSize: "0.75rem", color: "#64748b" }}>{status}</span>
    );
  }
  return (
    <div
      style={{ padding: "6px 2px 2px", width: "100%" }}
      {...toggleRowExpandedProps}
      title="Status Details"
      alt="Status Details"
    >
      {isLoading ? (
        <>
          <style>{`
            @keyframes statusBarBlink {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
          `}</style>
          <div style={{ display: "flex", alignItems: "flex-start" }}>
            {[0, 1, 2, 3].map((index) => (
              <React.Fragment key={index}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 5,
                    flexShrink: 0,
                    width: 30,
                  }}
                >
                  <div //circles
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      background: "#dee2e6", // #92d0aa //greenish color that also looks kinda nice for all the elements with the same color
                      border: "2px solid #e2e8f0",
                      animation: `statusBarBlink 1.2s ease-in-out infinite`,
                      animationDelay: `${index * 0.15}s`,
                    }}
                  />
                  {/* <span //text/label
                    style={{
                      width: 28,
                      height: 6,
                      borderRadius: 3,
                      background: "#dee2e6",
                      display: "block",
                      animation: `statusBarBlink 1.2s ease-in-out infinite`,
                      animationDelay: `${index * 0.15}s`,
                    }}
                  /> */}
                </div>
                {index < 3 && (
                  <div //lines between circles
                    style={{
                      flex: 1,
                      height: 2,
                      alignSelf: "flex-start",
                      marginTop: 14,
                      background: "#dee2e6",
                      borderRadius: 1,
                      animation: `statusBarBlink 1.2s ease-in-out infinite`,
                      animationDelay: `${index * 0.15 + 0.075}s`,
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </>
      ) : isError ? (
        <div>Error</div>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
          }}
        >
          {steps.map((step, index) => {
            const isComplete = currentLevel > index;
            const isActive = currentLevel === index;
            const isLastStep = index === steps.length - 1;
            const isExceptionStep = isException && isLastStep;
            const isWarningStep = isWarning && isLastStep;
            const isPartialStep = isPartial && isLastStep;
            const Icon = step.icon;

            // Per-step color tokens
            const bubbleBg = isExceptionStep
              ? "#fee2e2"
              : isWarningStep
                ? "#fefce8"
                : isPartialStep
                  ? "#dcfce7"
                  : isComplete
                    ? "#dcfce7"
                    : isActive
                      ? "#bbf7d0"
                      : "#f1f5f9";
            const bubbleBorder = isExceptionStep
              ? "2px solid #f87171"
              : isWarningStep
                ? "2px solid #fbbf24"
                : isPartialStep
                  ? "2px dashed #16a34a"
                  : isActive
                    ? "2px solid #16a34a"
                    : isComplete
                      ? "2px solid #4ade80"
                      : "2px solid #e2e8f0";
            const bubbleShadow = isExceptionStep
              ? "0 0 0 4px rgba(239,68,68,0.15), 0 2px 6px rgba(220,38,38,0.3)"
              : isWarningStep
                ? "0 0 0 4px rgba(251,191,36,0.2), 0 2px 6px rgba(245,158,11,0.3)"
                : isPartialStep
                  ? "0 2px 6px rgba(74,222,128,0.35)"
                  : isActive
                    ? "0 0 0 4px rgba(22,163,74,0.2), 0 2px 6px rgba(21,128,61,0.3)"
                    : isComplete
                      ? "0 2px 6px rgba(74,222,128,0.35)"
                      : "none";
            const iconColor = isExceptionStep
              ? "#dc2626"
              : isWarningStep
                ? "#d97706"
                : isComplete
                  ? "#16a34a"
                  : isActive
                    ? "#15803d"
                    : "#cbd5e1";
            const labelColor = isExceptionStep
              ? "#dc2626"
              : isWarningStep
                ? "#d97706"
                : isComplete
                  ? "#16a34a"
                  : isActive
                    ? "#15803d"
                    : "#94a3b8";

            return (
              <React.Fragment key={step.label}>
                {/* Step node */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 5,
                    flexShrink: 0,
                    width: 30,
                  }}
                >
                  {/* Icon bubble */}
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      background: bubbleBg,
                      border: bubbleBorder,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: bubbleShadow,
                      transition: "all 0.3s ease",
                      flexShrink: 0,
                    }}
                  >
                    <Icon
                      size={13}
                      color={iconColor}
                      strokeWidth={
                        isActive ||
                        isExceptionStep ||
                        isWarningStep ||
                        isPartialStep
                          ? 2.5
                          : 2
                      }
                    />
                  </div>

                  {/* Label */}
                  <span
                    style={{
                      fontSize: "0.5rem",
                      color: labelColor,
                      fontWeight:
                        isActive ||
                        isExceptionStep ||
                        isWarningStep ||
                        isPartialStep
                          ? 700
                          : isComplete
                            ? 600
                            : 400,
                      textAlign: "center",
                      maxWidth: 50,
                      lineHeight: 1.25,
                      letterSpacing: "0.02em",
                    }}
                  >
                    {step.label}
                  </span>
                </div>

                {/* Connector line */}
                {index < steps.length - 1 && (
                  <div
                    style={{
                      flex: 1,
                      height: 2,
                      alignSelf: "flex-start",
                      marginTop: 14,
                      background: isComplete
                        ? "linear-gradient(90deg, #4ade80, #16a34a)"
                        : "repeating-linear-gradient(90deg, #cbd5e1 0, #cbd5e1 3px, transparent 3px, transparent 7px)",
                      borderRadius: 1,
                      transition: "background 0.3s ease",
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};
